import React, { useCallback, useContext } from "react";
import { Draggable, type DraggableChildrenFn, Droppable } from "react-beautiful-dnd";
import { graphql, useFragment } from "react-relay";
import { useHistory } from "react-router-dom";

import {
	faCalendarRange as falCalendarRange,
	faStar as falStar,
	faUserFriends as falUserFriends,
	faUsers as falUsers,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faCalendarRange as farCalendarRange,
	faStar as farStar,
	faUserPlus,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserFriends as farUserFriends, faUsers as farUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compact } from "lodash";

import moment from "~/lib/extendedMoment";
import { useMedia } from "~/lib/hooks";
import { isNotOther } from "~/lib/relayUtil";
import type { NodesFromEdges } from "~/lib/utils";
import { HomePanelContext } from "~/nav/panels/Home";
import OneOnOneLink from "~/nav/panels/Home/Links/OneOnOneLink";
import StreamLink from "~/nav/panels/Home/Links/StreamLink";
import TagLink from "~/nav/panels/Home/Links/TagLink";
import EmptyState from "~/nav/panels/Home/Sections/EmptyState";
import TeamsSection from "~/nav/panels/Home/Sections/TeamsGroup";
import PanelSection from "~/nav/panels/shared/PanelSection";
import CreateStreamModal from "~/stream/CreateStreamModal";
import CreateTeamModal from "~/team/CreateOrEditTeam";
import { Button } from "~/ui";
import BetaLabel from "~/ui/labels/BetaLabel";
import { useModals } from "~/ui/modals";

import MeetingsGroup from "./MeetingsGroup";

import type {
	HomePanelGroup_group$key as GroupFragment,
	HomePanelGroup_group$data as GroupFragmentData,
	HomePanelGroupType,
} from "~/__generated__/HomePanelGroup_group.graphql";
import type { HomeQuery } from "~/__generated__/HomeQuery.graphql";

import panelSectionStyles from "~/nav/panels/shared/PanelSection.module.css";

/**
 * Will a given HomePanelGroup accept dropping a given object? If no object is provided, will the
 * group accept dropping any objects at all?
 */
export function willGroupAcceptObject(
	group: { type: HomePanelGroupType },
	obj?: { __typename: string; type?: string } | null,
) {
	if (group.type === "MEETINGS" || group.type === "TEAMS") return false;
	if (group.type === "ONE_ON_ONE") {
		if (!obj) return true;
		else if (obj.__typename === "User") return true;
		else if (obj.__typename === "NoteStream" && obj.type === "oneonone") return true;
		else return false;
	}
	if (group.type === "OTHER_STREAMS") {
		if (!obj) return true;
		if (obj.__typename === "Tag") return true;
	}
	return true;
}

// Really this name hardcoding should probably be in the backend, but it's here now.
export function determineGroupName(group: { readonly name: string; readonly type: HomePanelGroupType }): string {
	switch (group.type) {
		case "MEETINGS":
			return "Meetings";
		case "TEAMS":
			return "Teams";
		case "ONE_ON_ONE":
			return "1-on-1s";
		case "OTHER_STREAMS":
			return "Series";
		default:
			return group.name;
	}
}

export function determineGroupIcon(
	group: {
		readonly type: HomePanelGroupType;
		readonly emojiName: string | null;
	},
	weight: "regular" | "light" = "regular",
): IconDefinition | string {
	switch (group.type) {
		case "MEETINGS":
			return weight === "regular" ? farCalendarRange : falCalendarRange;
		case "TEAMS":
			return farUsers;
			return weight === "regular" ? farUsers : falUsers;
		case "ONE_ON_ONE":
			return weight === "regular" ? farUserFriends : falUserFriends;
		case "OTHER_STREAMS":
			return weight === "regular" ? farStar : falStar;
		default:
			if (group.emojiName) return group.emojiName;

			return weight === "regular" ? farStar : falStar;
	}
}

type GroupItem = NodesFromEdges<GroupFragmentData["items"]>;
// Defined here so it can be reused as Droppable's renderClone prop - see:
// https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md#cloning-api
const getDraggableInnerRenderer = (items: readonly GroupItem[]): DraggableChildrenFn =>
	function StreamLinkClone(provided, _snapshot, rubric) {
		const item = items[rubric.source.index];
		if (item.__typename === "%other") throw new Error("Unrecognized item in Home panel");
		if (item.__typename === "Tag") {
			return (
				<TagLink
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					// className={cx({ [styles.dragging]: snapshot.isDragging })}
					ref={provided.innerRef}
					key={item.id}
					tag={item}
				/>
			);
		} else if (item.__typename === "NoteStream") {
			return (
				<StreamLink
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					// className={cx({ [styles.dragging]: snapshot.isDragging })}
					innerRef={provided.innerRef}
					key={item.id}
					stream={item}
				/>
			);
		} else {
			return (
				<OneOnOneLink
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					// className={cx({ [styles.dragging]: snapshot.isDragging })}
					innerRef={provided.innerRef}
					key={item.id}
					user={item}
				/>
			);
		}
	};

interface HomePanelGroupProps extends Omit<React.HTMLProps<HTMLDivElement>, "ref"> {
	group: GroupFragment;
	viewer: HomeQuery["response"]["viewer"];
	isDragging: boolean;
	// this is because the invite modal doesn't use the new system and properly moving it to this component would take too much time,
	// so I just pass in a reference to the 1on1 plus button function that does the work for us :)
	oneOnOnePlusButtonClick: () => void;
}

const HomePanelGroup = React.forwardRef<HTMLDivElement, HomePanelGroupProps>(function HomePanelGroup(
	{ group: _group, viewer, isDragging, oneOnOnePlusButtonClick, ...rest },
	ref,
) {
	const group = useFragment(
		graphql`
			fragment HomePanelGroup_group on HomePanelGroup {
				id
				type
				name
				emojiName
				items {
					edges {
						node {
							__typename
							... on User {
								id
								...OneOnOneLink_user
							}
							... on NoteStream {
								# this has to be inside '... on TypeName' for the generated types to
								# use a discriminated union
								...StreamLink_stream
								id
								archived
								starred
								viewerLastSeenAt
								latestNoteEditedAt
							}
							... on Tag {
								__typename
								id
								label
								color
								...TagLink_tag
							}
						}
					}
				}
			}
		`,
		_group,
	) as GroupFragmentData | null;

	const items = compact(compact(group?.items.edges).map(e => e.node)).filter(isNotOther);

	const unread = items.some(function itemIsUnread(event) {
		if ("latestNoteEditedAt" in event) {
			if (Boolean(event.latestNoteEditedAt && moment(event.viewerLastSeenAt).isBefore(event.latestNoteEditedAt))) {
				return event;
			}
		}

		return;
	});

	const panelContext = useContext(HomePanelContext);
	const modals = useModals();
	const history = useHistory();

	const draggableInnerRenderer = React.useMemo(() => getDraggableInnerRenderer(items), [items]);

	const onStreamCreated = useCallback(
		(streamId: string) => {
			history.push(`/stream/${streamId}/`);
		},
		[history],
	);
	const createStreamInGroup = useCallback(() => {
		viewer &&
			modals.open(({ close }) => (
				<CreateStreamModal viewer={viewer} onClose={close} onCreated={onStreamCreated} groupId={group?.id} />
			));
	}, [modals, viewer, onStreamCreated, group?.id]);

	const onTeamCreated = useCallback(
		(teamId: string) => {
			history.push(`/teams/${teamId}/`);
		},
		[history],
	);

	const showCreateTeamModal = useCallback(() => {
		modals.open(({ close }) => <CreateTeamModal onClose={close} onCreated={onTeamCreated} />);
	}, [modals, onTeamCreated]);

	const supportsHover = useMedia("(hover: hover)");

	// TODO: Remove this once all the teams group records are removed from database
	const shouldNotShowTeamsGroup = group?.type === "TEAMS";

	if (!group) return null;

	const name = determineGroupName(group);
	const icon = group.type === "OTHER_STREAMS" ? undefined : determineGroupIcon(group);

	const isDropDisabled = !willGroupAcceptObject(group, panelContext.currentlyDragging ?? undefined);
	// The group should only fade if we're *currently* dragging an object it won't accept, not including
	// another group, or itself
	const fadeSection =
		isDropDisabled &&
		!!panelContext.currentlyDragging &&
		panelContext.currentlyDragging.__typename !== "HomePanelGroup";

	if (group.type === "TEAMS") {
		// instead of null, we need to return a div with a ref so that the droppable can be rendered
		// this is necessary to make the drag & drop feature work properly
		// we can't just remove the teams group from the group list because changing the order wouldn't work without it
		if (shouldNotShowTeamsGroup) {
			return <div ref={ref}></div>;
		}

		return (
			<PanelSection
				title={name}
				afterTitle={<BetaLabel />}
				icon={icon}
				dropDisabledFade={fadeSection}
				group={group}
				plusButtonTooltip={"Create team"}
				isDragging={isDragging}
				onPlusClick={showCreateTeamModal}
				{...rest}
				ref={ref}
			>
				<TeamsSection items={panelContext.teams} />
			</PanelSection>
		);
	} else if (group.type === "MEETINGS") {
		return (
			<MeetingsGroup
				group={group}
				isDragging={isDragging}
				isDropDisabled={isDropDisabled}
				dropDisabledFade={fadeSection}
				{...rest}
				ref={ref}
			/>
		);
	}

	let onPlusClick: () => void;
	let plusButtonTooltip;
	let plusButtonTestId;

	if (group.type === "ONE_ON_ONE") {
		onPlusClick = oneOnOnePlusButtonClick;
		plusButtonTooltip = "Add 1-on-1s";
		plusButtonTestId = "new-1on1-button";
	} else {
		onPlusClick = createStreamInGroup;
		plusButtonTooltip = "New Series";
		plusButtonTestId = group.type === "OTHER_STREAMS" ? "new-personal-stream-button" : undefined;
	}

	return (
		<PanelSection
			title={name}
			icon={icon}
			plusButtonTestId={plusButtonTestId}
			dropDisabledFade={fadeSection}
			group={group}
			plusButtonTooltip={plusButtonTooltip}
			onPlusClick={onPlusClick}
			ref={ref}
			unread={unread}
			isDragging={isDragging}
			{...rest}
		>
			<Droppable
				type="ITEM"
				droppableId={group.id}
				renderClone={draggableInnerRenderer}
				isDropDisabled={isDropDisabled}
			>
				{provided => (
					<div {...provided.droppableProps} ref={provided.innerRef}>
						{items.length > 0 &&
							items.map((item, index) => {
								return (
									<Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={!supportsHover}>
										{draggableInnerRenderer}
									</Draggable>
								);
							})}
						{provided.placeholder}
						{items.length === 0 && group.type !== "ONE_ON_ONE" && <EmptyState emptyStateText="Group is empty" />}
						{group.type === "ONE_ON_ONE" && (
							<Button size="wide" variant="tertiary" onClick={onPlusClick} className={panelSectionStyles.menuLink}>
								<FontAwesomeIcon icon={faUserPlus} fixedWidth />
								Add your {items.length > 0 ? "teammates" : "manager"}…
							</Button>
						)}
					</div>
				)}
			</Droppable>
		</PanelSection>
	);
});

export default HomePanelGroup;
